import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styled, { keyframes } from "styled-components";
import { fetchPublicProducts } from "../../../../services/public.services";
import { setProduct } from "../../../../store/product/product.Slice";
import { useAppDispatch } from "../../../../store/store";

gsap.registerPlugin(ScrollTrigger);

interface Product {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string | null;
  productCategory: { name: string };
  productMappedBrand: { name: string };
}

const FeaturedProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const sectionRef = useRef<HTMLElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetchPublicProducts({ page: 1, limit: 4 });
        setProducts(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching featured products:", error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section.querySelectorAll(".featured-card"),
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, [products]);

  const onProductClick = (productName: string) => {
    dispatch(setProduct(productName));

    // If already on home page, just scroll to contactUs
    if (location.pathname === "/") {
      const element = document.getElementById("contactUs");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // Navigate to home page with hash
      navigate("/#contactUs");
    }
  };

  const badges = ["Hot", "New", "Best Seller", "Featured"];

  if (products.length === 0) return null;

  return (
    <FeaturedSection ref={sectionRef}>
      <div className="container">
        <div className="section-header">
          <div className="badge-label">
            <span className="fire-icon">🔥</span>
            <span>Featured Products</span>
          </div>
          <h2>
            Discover Our <span className="highlight">Top Picks</span>
          </h2>
          <p>Hand-picked products for quality and performance</p>
        </div>

        <div className="featured-grid">
          {products.slice(0, 4).map((product, index) => (
            <div
              key={product.id}
              className="featured-card"
              onClick={() => onProductClick(product.name)}
            >
              <div className="badge">{badges[index % badges.length]}</div>
              <div className="image-wrapper">
                <img
                  src={product.thumbnailUrl || ""}
                  alt={product.name}
                />
              </div>
              <div className="content">
                <span className="brand">{product.productMappedBrand?.name}</span>
                <h3>{product.name}</h3>
                <button className="enquire-btn">
                  Enquire Now
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </FeaturedSection>
  );
};

export default FeaturedProducts;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const FeaturedSection = styled.section`
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  padding: 100px 20px;
  position: relative;
  overflow: hidden;

  .container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .section-header {
    text-align: center;
    margin-bottom: 60px;

    .badge-label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: linear-gradient(135deg, rgba(217, 58, 53, 0.15) 0%, rgba(217, 58, 53, 0.05) 100%);
      padding: 10px 24px;
      border-radius: 50px;
      margin-bottom: 20px;
      border: 1px solid rgba(217, 58, 53, 0.2);

      .fire-icon {
        font-size: 18px;
        animation: ${pulse} 1.5s ease-in-out infinite;
      }

      span {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        color: var(--accent);
        text-transform: uppercase;
        letter-spacing: 2px;
      }
    }

    h2 {
      font-family: Poppins;
      font-size: 48px;
      font-weight: 800;
      color: var(--text-primary);
      margin: 0 0 16px;

      .highlight {
        background: linear-gradient(135deg, var(--accent) 0%, var(--primary) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    p {
      font-family: Poppins;
      font-size: 18px;
      color: var(--text-secondary);
      margin: 0;
    }
  }

  .featured-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 30px;
  }

  .featured-card {
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    border: 2px solid var(--card-border);
    cursor: pointer;
    transition: all 0.4s ease;
    position: relative;

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 25px 50px var(--shadow-large);
      border-color: var(--accent);

      .image-wrapper img {
        transform: scale(1.1);
      }

      .enquire-btn {
        background: var(--accent);
        color: #fff;
      }
    }

    .badge {
      position: absolute;
      top: 16px;
      left: 16px;
      background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
      color: #fff;
      font-family: Poppins;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding: 6px 14px;
      border-radius: 6px;
      z-index: 2;
      box-shadow: 0 4px 15px rgba(217, 58, 53, 0.4);
    }

    .image-wrapper {
      height: 200px;
      background: var(--bg-secondary);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      img {
        max-height: 160px;
        max-width: 80%;
        object-fit: contain;
        transition: transform 0.5s ease;
      }
    }

    .content {
      padding: 24px;

      .brand {
        display: inline-block;
        font-family: Poppins;
        font-size: 12px;
        font-weight: 600;
        color: var(--primary);
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 8px;
      }

      h3 {
        font-family: Poppins;
        font-size: 18px;
        font-weight: 700;
        color: var(--text-primary);
        margin: 0 0 16px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.4;
      }

      .enquire-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        justify-content: center;
        padding: 12px 20px;
        background: transparent;
        border: 2px solid var(--accent);
        color: var(--accent);
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.3s ease;

        svg {
          width: 16px;
          height: 16px;
          transition: transform 0.3s ease;
        }

        &:hover svg {
          transform: translateX(4px);
        }
      }
    }
  }

  @media (max-width: 1200px) {
    .featured-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    padding: 80px 20px;

    .section-header {
      h2 {
        font-size: 36px;
      }

      p {
        font-size: 16px;
      }
    }

    .featured-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  @media (max-width: 480px) {
    .section-header h2 {
      font-size: 28px;
    }
  }
`;
